import { z } from "zod";

export const LEGAL_FORM_MESSAGES = {
  email: "Bitte gib eine gültige E-Mail-Adresse ein.",
  firstName: "Bitte gib deinen Vornamen ein.",
  lastName: "Bitte gib deinen Nachnamen ein.",
  cancellationStatement: "Bitte gib eine kurze Mitteilung zu deiner Kündigung ein.",
  withdrawalStatement: "Bitte gib eine kurze Mitteilung zu deinem Widerruf ein.",
} as const;

export const LegalAddressSchema = z.object({
  street: z.string().min(1, "Bitte gib deine Straße und Hausnummer ein."),
  postalCode: z.string().min(1, "Bitte gib deine Postleitzahl ein."),
  city: z.string().min(1, "Bitte gib deinen Ort ein."),
  country: z.string().min(1, "Bitte gib dein Land ein."),
});

export type LegalAddress = z.infer<typeof LegalAddressSchema>;

const isLegalAddressEmpty = (address: { street: string; postalCode: string; city: string; country: string }): boolean =>
  !address.street.trim() && !address.postalCode.trim() && !address.city.trim() && !address.country.trim();

const emptyStringToUndefined = (value: string | null | undefined): string | undefined => {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : undefined;
};

/** Callable payloads may send `null` for omitted optional strings — treat like absent. */
export const createLegalOptionalStringSchema = () => z.string().nullish().transform(emptyStringToUndefined);

/** Accepts omitted, null, or blank address input; validates full address only when any field is present. */
export const LegalOptionalAddressSchema = z
  .object({
    street: z.string().nullish(),
    postalCode: z.string().nullish(),
    city: z.string().nullish(),
    country: z.string().nullish(),
  })
  .nullish()
  .superRefine((address, context) => {
    if (!address) {
      return;
    }

    const normalized = {
      street: address.street?.trim() ?? "",
      postalCode: address.postalCode?.trim() ?? "",
      city: address.city?.trim() ?? "",
      country: address.country?.trim() ?? "",
    };

    if (isLegalAddressEmpty(normalized)) {
      return;
    }

    const result = LegalAddressSchema.safeParse(normalized);
    if (!result.success) {
      result.error.issues.forEach((issue) => context.addIssue({ ...issue }));
    }
  })
  .transform((address) => {
    if (!address) {
      return undefined;
    }

    const normalized = {
      street: address.street?.trim() ?? "",
      postalCode: address.postalCode?.trim() ?? "",
      city: address.city?.trim() ?? "",
      country: address.country?.trim() ?? "",
    };

    if (isLegalAddressEmpty(normalized)) {
      return undefined;
    }

    return LegalAddressSchema.parse(normalized);
  });

export const LegalPersonalDetailsSchema = z.object({
  email: z.email({ message: LEGAL_FORM_MESSAGES.email }),
  firstName: z.string().min(1, LEGAL_FORM_MESSAGES.firstName),
  lastName: z.string().min(1, LEGAL_FORM_MESSAGES.lastName),
  address: LegalOptionalAddressSchema,
  phone: createLegalOptionalStringSchema(),
});

export type LegalPersonalDetails = z.infer<typeof LegalPersonalDetailsSchema>;

export const createLegalStatementSchema = (message: string): z.ZodString => z.string().min(1, message);

/** Optional free-text statement; blank, null, or omitted input is treated as absent. */
export const createLegalOptionalStatementSchema = () => z.string().nullish().transform(emptyStringToUndefined);
